/**
 * Highlights the searched term in the Degree Typeahead List.
 * @param searchTerm string | search input value
 * @param typeaheadSearchResult string | degree typeahead search result item
 * @returns string with marked/highlighted searchTerm
 * @example *
 * highlightSearchTerm("bus us", "business in US") returns "<mark>bus</mark>iness in <mark>US</mark>"
 */
export const highlightSearchTerm = (searchTerm: string, typeaheadSearchResult: string): string => {
  const searchTermArray = searchTerm.split(/\s+/).filter((term) => !!term?.trim());
  const typeaheadSearchResultArray = typeaheadSearchResult
    .split(/\s+/)
    .filter((term) => !!term?.trim());
  const searchTermRegexes: RegExp[] = searchTermArray.map(
    (term) => new RegExp(`^((?!(<mark>)|<\\/mark>)(${term}))`, `gi`),
  );

  const highlightedSearchResult = typeaheadSearchResultArray.reduce(
    (highlightedResults: string, currentResult: string) => {
      // Each searchResult word may only match one term
      const matchingRegex = searchTermRegexes.find((searchTermRegex) =>
        searchTermRegex.test(currentResult),
      );
      if (matchingRegex) {
        const highlightedResult = currentResult.replace(matchingRegex, `<mark>$1</mark>`);
        return `${highlightedResults}${highlightedResult} `;
      }
      return `${highlightedResults}${currentResult} `;
    },
    ``,
  );

  return highlightedSearchResult;
};

/**
 * Finds last position of the first term in a word that matches the start of the word
 * @param searchTerms string[] | seach input split into individual terms
 * @param word string | current word to search
 * @returns length of found term or 0 if no term is found
 */
export const firstFoundTermEndPosition = (searchTerms: string[], word: string): number => {
  const foundTerm = searchTerms.find((term) => word.indexOf(term) === 0);
  if (foundTerm) {
    return foundTerm.length;
  }
  return 0;
};

export const highLightSearchTermEndPositions = (
  searchTerm: string,
  typeaheadSearchResult: string,
): Array<[string, number]> => {
  const searchTermArray = searchTerm
    .split(/\s+/)
    .filter((term) => !!term?.trim())
    .map((t) => t.toLowerCase());
  const typeaheadSearchResultArray = typeaheadSearchResult
    .split(/\s+/)
    .filter((term) => !!term?.trim());

  return typeaheadSearchResultArray.map((searchResult) => [
    searchResult,
    firstFoundTermEndPosition(searchTermArray, searchResult.toLowerCase()),
  ]);
};
